import { useId, useState } from 'react';
import { Box, Button, Collapse, Stack, Typography } from '@mui/material';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import { NoteDetail } from '../../../../Models/ReWeightingModels/Workspace';
import { downloadReweightingPdf } from '../../../../Services/ReweightingWorkspaceService';
import DocumentActions from '../../../../SharedComponents/Documents/DocumentActions';
import WeightLinesTable from './WeightLinesTable';
import { formatKg } from '../../workspaceFormatting';

export default function SourceNotesSection({ detail, disabled }: { detail: NoteDetail; disabled: boolean }) {
  const titleId = useId();
  const comparisonId = useId();
  const [expanded, setExpanded] = useState(false);
  const sources = detail.documents?.sourceNotes ?? [];
  const originalQuantity = detail.originalWeights.reduce((sum, line) => sum + line.quantity, 0);
  if (!detail.documents && !detail.originalWeights.length) return null;
  return <Box component="section" aria-labelledby={titleId} sx={{ mb: 3, pb: 2.5, borderBottom: 1, borderColor: 'divider' }}>
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
      <DescriptionOutlined sx={{ color: 'text.secondary', fontSize: 20 }} />
      <Typography id={titleId} component="h2" fontWeight={700} fontSize={15}>{detail.documents ? 'Til endurvigtunar' : 'Upprunaleg skráning'}</Typography>
    </Stack>
    {sources.length > 1 && <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>Hafnarnótur fyrirtækisins á þessari löndun.</Typography>}
    {sources.map(source => <Stack key={source.id} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" gap={1}
      sx={{ py: 1, '& + &': { borderTop: 1, borderColor: 'divider' } }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>Nóta #{source.noteNumber ?? source.id} · {source.buyerName}</Typography>
        {source.name !== 'Til endurvigtunar' && <Typography variant="caption" color="text.secondary">{source.name}</Typography>}
      </Box>
      <DocumentActions fileName={`vigtarnota_${source.id}.pdf`} previewLabel="Skoða nótu" load={() => downloadReweightingPdf(detail.note.id, source.id)} disabled={disabled} />
    </Stack>)}
    {detail.documents && sources.length === 0 && <Typography variant="body2" color="text.secondary">Engin hafnarnóta til endurvigtunar fannst á þessari löndun.</Typography>}
    {detail.originalWeights.length > 0 && <>
      <Button size="small" onClick={() => setExpanded(value => !value)} aria-expanded={expanded} aria-controls={comparisonId}
        sx={{ mt: 0.5, ml: -1, color: 'text.secondary', fontWeight: 500 }}
        endIcon={<ExpandMoreRounded sx={{ transform: expanded ? 'rotate(180deg)' : undefined, transition: 'transform 160ms ease', '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }} />}>
        Upprunalegar línur · {formatKg(originalQuantity)} kg
      </Button>
      <Collapse in={expanded} timeout="auto" unmountOnExit id={comparisonId}>
        <Box sx={{ mt: 1.5 }}><WeightLinesTable lines={detail.originalWeights} showDeadlines label="Upprunalegar vigtarlínur" /></Box>
      </Collapse>
    </>}
  </Box>;
}
